import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/** Keyboard hint, e.g. ⌘K. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-4 items-center rounded-xs border border-border bg-surface px-1 font-mono text-xs text-fg-subtle",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export function Spinner({ className, size = 12 }: { className?: string; size?: number }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{ width: size, height: size }}
      className={cn(
        "inline-block animate-spin rounded-full border-2 border-accent border-r-transparent",
        className,
      )}
    />
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Muted denominator or unit shown after the value, e.g. "/322". */
  suffix?: ReactNode;
  sub?: ReactNode;
  delta?: { text: string; tone: "ok" | "warn" | "err" | "neutral" };
  tone?: "default" | "err" | "warn";
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
}

const deltaTone = {
  ok: "text-ok-fg",
  warn: "text-warn-fg",
  err: "text-err-fg",
  neutral: "text-fg-subtle",
};

/** KPI tile. Clickable tiles act as filters and get a hover state. */
export function StatTile({
  label,
  value,
  suffix,
  sub,
  delta,
  tone = "default",
  onClick,
  children,
  className,
}: StatTileProps) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "flex flex-col gap-1 rounded-md border bg-surface px-4 py-3 text-left",
        tone === "err"
          ? "border-err-border"
          : tone === "warn"
            ? "border-warn-border"
            : "border-border",
        onClick &&
          "transition-colors hover:bg-hover focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
    >
      <span className="text-base font-medium text-fg-muted">{label}</span>
      <span className="flex items-baseline gap-1.5">
        <span
          className={cn(
            "text-2xl leading-7 font-semibold tracking-tight",
            tone === "err" && "text-err-fg",
          )}
        >
          {value}
        </span>
        {suffix ? <span className="text-fg-subtle">{suffix}</span> : null}
        {delta ? (
          <span className={cn("text-sm font-medium", deltaTone[delta.tone])}>{delta.text}</span>
        ) : null}
      </span>
      {sub ? <span className="text-sm text-fg-subtle">{sub}</span> : null}
      {children}
    </Comp>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center gap-1.5 px-6 py-12 text-center", className)}>
      <span className="font-medium text-fg">{title}</span>
      {description ? <span className="max-w-md text-fg-muted">{description}</span> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** Card with the canvas anatomy: bordered surface, optional header row. */
export function Card({
  title,
  meta,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cn("overflow-hidden rounded-md border border-border bg-surface", className)}
    >
      {title ? (
        <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-2.5">
          <h3 className="font-semibold text-fg">{title}</h3>
          {meta ? <span className="text-fg-subtle">{meta}</span> : null}
          <span className="flex-1" />
          {actions}
        </header>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
